import { inject, Injectable, Injector } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { DOCUMENT, Location } from '@angular/common';
import { EnvironmentService } from './environment.service';
import { GlobalEventsService } from './global-events.service';
import { distinct } from 'rxjs/operators';
import { REQUEST } from 'src/express.tokens';
import { environment } from 'src/environments/environment';
import { PlatformService } from 'ngx-unificator/services';
import { ROOT_LANG } from '../helpers/router';
import { isMirror, isVipMirror } from '../helpers/mirror';

@Injectable({
  providedIn: 'root',
})
export class SeoService {

  private _meta = inject(Meta);
  private _title = inject(Title);
  private _platform = inject(PlatformService);
  private _location = inject(Location);
  private _env = inject(EnvironmentService);
  private _globalEvents = inject(GlobalEventsService);
  private _injector = inject(Injector);
  private _document = inject<Document>(DOCUMENT);

  private _alternateLocalesForHeader = [ROOT_LANG, 'fr-ca', 'en-au'];

  private _mapPrefixMirrorsLoacales: Record<string, string> = {
    'en-au': 'au',
  };

  /**
   * Returns hostname on which app running
   *
   * @private
   */
  private _getAppHost(): string | undefined {
    const host = this._platform.isBrowser ? window.location.host : this._injector.get(REQUEST).headers.host;
    return `https://${host}`;
  }

  public get appHost(): string {
    return this._getAppHost();
  }

  /**
   * Set in head Title & description
   * @param tags
   */
  public setMetaTags(tags: any) {
    this._title.setTitle(tags.MetaTitle || 'Lets go casino');
    this._meta.updateTag({ name: 'description', content: tags.MetaDesc || '' });

    if ('NoIndex' in tags && tags.NoIndex) {
      this.addMetaRobotNoIndex();
    }
  }

  /**
   * Add new alternate/canonical links and remove old links
   */
  public updateGlobalHeadLink() {
    this._addLinkPreconnect();
    this._resolveIsMirror();
    this._globalEvents.routerNavigationEnd$.pipe(distinct()).subscribe(e => {
      this._removeOldLinks(['alternate', 'canonical', 'x-default']);
      this._addHeadLinkAlternate();
      this._addHeadLinkCanonical('canonical');
      this._addHeadLincXDefault();
      this._addLangTagForHTML();
    });
  }

  /**
   * Add link alternate <link rel="alternate" href="https://www.example.com/" hreflang="en-ca">
   */
  private _addHeadLinkAlternate() {
    this._alternateLocalesForHeader.forEach(e => {
      const link: HTMLLinkElement = this._document.createElement('link');
      const url = this._getLocalePath(e);
      link.setAttribute('rel', 'alternate');
      if (e !== ROOT_LANG) {
        if (this._mapPrefixMirrorsLoacales[e]) {
          link.setAttribute('href', this._overrideMirrorDomain(environment.app_host, this._mapPrefixMirrorsLoacales[e]) + url);
          link.setAttribute('hreflang', e);
        } else {
          link.setAttribute('href', environment.app_host + url);
          link.setAttribute('hreflang', e);
        }
      } else {
        link.setAttribute('href', environment.app_host + url);
        link.setAttribute('hreflang', e);
      }
      this._document.head.appendChild(link);
    });
  }

  /**
   * Adds a canonical link element to the document head, pointing to the appropriate URL for the current page.
   * The URL is constructed based on the current URL path, ensuring that the canonical link points to the correct language version of the page.
   * This is important for SEO and helps search engines understand the relationship between different language versions of the same content.
   * @param attr The rel attribute value for the link element, typically 'canonical'.
   */
  private _addHeadLinkCanonical(attr: string) {
    const link: HTMLLinkElement = this._document.createElement('link');
    link.setAttribute('rel', attr);
    const currentPath = this._location.path();
    switch (true) {
      case this._isLocalePath(currentPath, 'en'):
      case this._isLocalePath(currentPath, ROOT_LANG):
        link.setAttribute('href', this._getAppHost() + this._getRootLocalePath());
        break;
      case this._isLocalePath(currentPath, 'en-au'):
        link.setAttribute(
          'href',
          this._overrideMirrorDomain(environment.app_host, this._mapPrefixMirrorsLoacales['en-au']) + this._getLocalePath('en-au'),
        );
        break;
      case !currentPath:
        link.setAttribute('href', this._getAppHost() + '/');
        break;
      default:
        link.setAttribute('href', this._getAppHost() + this._normalizeUrlPath(currentPath));
        break;
    }
    this._document.head.appendChild(link);
  }

  /**
   * Adds an 'x-default' link element to the document head, pointing to the canonical URL for the current page.
   * The URL is constructed by replacing any language-specific path segments (e.g. '/en/') with '/en'.
   * This ensures that the 'x-default' link points to the English version of the page, as per the recommended best practices for internationalization.
   */
  private _addHeadLincXDefault() {
    const link: HTMLLinkElement = this._document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('href', environment.app_host + this._getRootLocalePath());
    link.setAttribute('hreflang', 'x-default');
    this._document.head.appendChild(link);
  }

  private _getLocalePath(locale: string): string {
    if (locale === ROOT_LANG) {
      return this._getRootLocalePath();
    }

    const pathname = this._getPathname(this._getRootLocalePath());
    return `/${locale}${pathname}`.replace(/\/$/, '');
  }

  private _getRootLocalePath(): string {
    const pathname = this._getPathname(this._location.path());
    let path = pathname || '/';

    [...this._alternateLocalesForHeader, 'en'].forEach(lang => {
      if (path === `/${lang}`) {
        path = '/';
      }

      if (path.startsWith(`/${lang}/`)) {
        path = path.slice(lang.length + 1);
      }
    });

    return this._normalizeUrlPath(path);
  }

  private _normalizeUrlPath(path: string): string {
    if (!path) {
      return '/';
    }

    const pathname = this._getPathname(path);
    const normalizedPath = pathname.startsWith('/') ? pathname : `/${pathname}`;
    return normalizedPath.replace(/\/+$/, '') || '/';
  }

  private _isLocalePath(path: string, locale: string): boolean {
    const pathname = this._getPathname(path);
    return pathname === `/${locale}` || pathname.startsWith(`/${locale}/`);
  }

  private _getPathname(path: string): string {
    return (path || '').split(/[?#]/)[0];
  }

  /**
   * Remove old alternate\canonical links from DOM
   * @param typeOfLinks
   */
  private _removeOldLinks(typeOfLinks: any[]) {
    if (this._platform.isBrowser) {
      typeOfLinks.forEach(e => {
        const oldLinks: NodeListOf<HTMLLinkElement> = this._document.querySelectorAll(`link[rel="${e}"]`);
        if (oldLinks.length) {
          oldLinks.forEach(j => {
            j.remove();
          });
        }
      });
    }
  }

  /**
   * Add in head <meta name="robots" content="noindex" />
   */
  public addMetaRobotNoIndex() {
    this._meta.addTags([{ name: 'robot', content: 'noindex' }]);
  }

  private _addLinkPreconnect() {
    const link: HTMLLinkElement = this._document.createElement('link');
    const mediaDomain = this._getAppHost()
      .replace('www.', 'media.')
      .replace('stage.', 'stage-media.');
    link.setAttribute('rel', 'preconnect');
    link.setAttribute('href', mediaDomain);
    this._document.head.appendChild(link);
  }

  /**
   * Sets the `lang` attribute of the HTML `<html>` element based on the current URL path.
   * If the path contains a language-specific segment (e.g. `/en/`), the corresponding language code is used.
   * Otherwise, the default language code (`'en'`) is used.
   * This ensures that the correct language is specified for the page, which is important for accessibility and SEO.
   */
  private _addLangTagForHTML() {
    const matches = this._location.path().split('/');
    const locale = matches && this._env
        .dataEnv()
        .languageList?.map(e => e?.short)
        .includes(matches[1])
        ? matches[1]
        : ROOT_LANG;
    this._document.documentElement.lang = locale;
  }

  /**
   * Overrides the mirror domain by appending the provided mirror prefix to the second-to-last part of the domain.
   * @param domain The original domain to be overridden.
   * @param mirrorPrefix The prefix to be appended to the second-to-last part of the domain.
   * @returns The overridden domain with the mirror prefix applied.
   */
  private _overrideMirrorDomain(domain: string, mirrorPrefix: string) {
    const parts = domain.split('.');
    if (parts.length >= 2) {
      parts[parts.length - 2] += mirrorPrefix;
    }
    return parts.join('.');
  }

  /**
   * The function `_resolveIsMirror` determines if the current domain is a mirror site based on the host
   * information.
   */
  private _resolveIsMirror() {
    const host = this._platform.isBrowser ? window.location.host : this._injector.get(REQUEST).headers.host;
    const parts = host
      .replace(/^www\./, '')
      .split(':')[0]
      .split('.');
    const domain = parts.length > 2 && parts[parts.length - 2].length <= 3
      ? parts[parts.length - 3]
      : parts.length > 1
        ? parts[parts.length - 2]
        : parts[0];
    isVipMirror.set('vipletsgocasino' === domain);
    isMirror.set(domain !== 'letsgocasino' && !isVipMirror());
  }
}
